import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Switch,
  Table,
  Text,
  Title,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconBox,
  IconPlayerPlay,
  IconPlayerStop,
  IconPower,
} from "@tabler/icons-react";
import {
  apiDelete,
  apiFetch,
  apiPost,
  friendlyError,
  type ApiError,
} from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";
import { StatusPill } from "../../components/StatusPill.js";
import { CreateContainerModal } from "./CreateContainerModal.js";
import { backupActionError, isApiError } from "./BackupsCard.js";
import {
  containerAction,
  formatBytes,
  formatRam,
  formatUptime,
  type ContainerAction,
  type ContainerListItem,
} from "./containerUtils.js";

const POLL_MS = 5000;

export function ContainersDashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [containers, setContainers] = useState<ContainerListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pveDown, setPveDown] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState<Set<number>>(new Set());
  const [createOpened, setCreateOpened] = useState(false);

  async function load() {
    try {
      const data = await apiFetch<ContainerListItem[]>("/api/containers");
      setContainers(data);
      setError(null);
      setPveDown(false);
      setLastUpdated(Date.now());
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 502 && apiErr.error === "pve_unavailable") {
        setPveDown(true);
        setError(null);
      } else {
        setPveDown(false);
        setError(friendlyError(err, "Could not load containers."));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  function setRowBusy(vmid: number, on: boolean) {
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(vmid);
      else next.delete(vmid);
      return next;
    });
  }

  async function runAction(
    ct: ContainerListItem,
    action: ContainerAction,
    body?: { force?: boolean; timeout?: number }
  ) {
    if (ct.vmid === null) return;
    setRowBusy(ct.vmid, true);
    try {
      const res = await containerAction(ct.vmid, action, body);
      notifications.show({ color: res.ok ? "green" : "red", message: res.message });
      if (res.ok) void load();
    } finally {
      setRowBusy(ct.vmid, false);
    }
  }

  function confirmPower(ct: ContainerListItem, action: "shutdown" | "stop") {
    if (ct.vmid === null) return;
    const isStop = action === "stop";
    modals.openConfirmModal({
      title: isStop ? "Force stop container" : "Shut down container",
      centered: true,
      children: (
        <Text size="sm">
          {isStop ? (
            <>
              Force stop <b>{ct.name}</b>? This pulls the power and may corrupt the filesystem.
            </>
          ) : (
            <>
              Shut down <b>{ct.name}</b>? The container will be asked to exit cleanly.
            </>
          )}
        </Text>
      ),
      labels: { confirm: isStop ? "Force stop" : "Shut down", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => void runAction(ct, action, isStop ? undefined : { force: false }),
    });
  }

  function onBackupSwitchChange(ct: ContainerListItem) {
    if (ct.vmid === null) return;
    if (!ct.backupEnabled) {
      void enableRowBackups(ct.vmid);
    } else {
      confirmRemoveBackups(ct.vmid, ct.name);
    }
  }

  async function enableRowBackups(vmid: number) {
    setRowBusy(vmid, true);
    try {
      try {
        const res = await apiPost<{ schedule: string }>(`/api/containers/${vmid}/backups`, {
          hour: 2,
          minute: 0,
        });
        notifications.show({
          color: "green",
          message: `Backups enabled — daily at ${res.schedule}`,
        });
      } catch (err) {
        if (isApiError(err) && err.status === 409 && err.error === "backup_job_exists") {
          // Another tab/poll enabled it first — reload; end state is the same.
        } else if (!isApiError(err)) {
          notifications.show({ color: "red", message: "Couldn't reach the server." });
          return;
        } else {
          const mapped = backupActionError(err, "Couldn't enable backups.");
          notifications.show({ color: "red", title: mapped.title, message: mapped.message });
          return;
        }
      }
      void load();
    } finally {
      setRowBusy(vmid, false);
    }
  }

  function confirmRemoveBackups(vmid: number, name: string) {
    modals.openConfirmModal({
      title: "Remove from backups",
      centered: true,
      children: (
        <Text size="sm">
          Daily backups stop for <b>{name}</b> — existing backups on the backup storage are
          kept. If it is the last server in the backup job, the whole job is deleted;
          re-enabling later starts a fresh job with the default 02:00 daily schedule and
          default retention.
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => void disableRowBackups(vmid),
    });
  }

  async function disableRowBackups(vmid: number) {
    setRowBusy(vmid, true);
    try {
      try {
        await apiDelete(`/api/containers/${vmid}/backups`);
      } catch (err) {
        if (!(isApiError(err) && err.status === 404 && err.error === "backup_job_not_found")) {
          notifications.show({
            color: "red",
            message: friendlyError(err, "Couldn't remove this server from backups."),
          });
          return;
        }
        // Already gone — same end state as a successful removal.
      }
      notifications.show({ color: "green", message: "Backups disabled" });
      void load();
    } finally {
      setRowBusy(vmid, false);
    }
  }

  const agoSec = lastUpdated !== null ? Math.max(0, Math.floor((now - lastUpdated) / 1000)) : null;
  const count = containers?.length ?? 0;

  return (
    <Box>
      <Group justify="space-between" mb="lg" align="flex-end" wrap="nowrap">
        <Stack gap={4}>
          <Title order={2}>Containers</Title>
          <Text size="sm" c="dimmed">
            {loading
              ? "Loading your containers…"
              : count === 0
                ? "No containers yet — deploy your first LXC container."
                : `${count} container${count === 1 ? "" : "s"}${
                    auth.session?.display_name ? ` for ${auth.session.display_name}` : ""
                  }`}
          </Text>
        </Stack>
        <Group gap="xs" wrap="nowrap">
          {agoSec !== null && (
            <Text size="xs" c="dimmed" visibleFrom="sm">
              Updated {agoSec}s ago
            </Text>
          )}
          <Button variant="default" size="sm" onClick={() => void load()} loading={loading}>
            Refresh
          </Button>
          <Button size="sm" leftSection={<IconBox size={16} stroke={1.75} />} onClick={() => setCreateOpened(true)}>
            New Container
          </Button>
        </Group>
      </Group>

      <CreateContainerModal opened={createOpened} onClose={() => setCreateOpened(false)} onCreated={() => void load()} />

      {error && (
        <Alert color="red" variant="light" mb="md" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}
      {pveDown && (
        <Alert color="orange" variant="light" mb="md" title="Proxmox unreachable">
          Can&apos;t reach Proxmox right now — retrying in the background. Showing last known state.
        </Alert>
      )}

      {loading && containers === null ? (
        <Stack gap="xs" align="center" py="xl">
          <Loader />
          <Text size="sm" c="dimmed">
            Loading your containers…
          </Text>
        </Stack>
      ) : count === 0 ? (
        <EmptyState onCreate={() => setCreateOpened(true)} />
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>CPU</Table.Th>
                <Table.Th>RAM</Table.Th>
                <Table.Th>Swap</Table.Th>
                <Table.Th>Disk</Table.Th>
                <Table.Th>Uptime</Table.Th>
                <Table.Th w={90}>Backups</Table.Th>
                <Table.Th w={110}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {containers?.map((ct) => {
                const isPending = ct.vmid === null;
                const isBusy = ct.vmid !== null && busy.has(ct.vmid);
                const rowKey = isPending ? `creation-${ct.creation_id ?? ct.name}` : `ct-${ct.vmid}`;
                const backupTooltip =
                  ct.backupEnabled && ct.backupPaused
                    ? "Backups paused by operator"
                    : !ct.backupOffered
                      ? "Backups not available — contact support"
                      : ct.backupEnabled
                        ? "Backups enabled — manage on the Backups card"
                        : "Enable daily backups (02:00 default)";
                return (
                  <Table.Tr
                    key={rowKey}
                    style={{
                      cursor: isPending ? "default" : "pointer",
                    }}
                    onClick={isPending ? undefined : () => navigate(`/dashboard/containers/${ct.vmid}`)}
                  >
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon
                          size="sm"
                          variant="light"
                          color="brand"
                          radius="md"
                          style={{ flex: "0 0 auto" }}
                        >
                          <IconBox size={15} stroke={1.75} />
                        </ThemeIcon>
                        <Stack gap={0}>
                          <Anchor
                            component="button"
                            type="button"
                            size="sm"
                            fw={600}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (!isPending) navigate(`/dashboard/containers/${ct.vmid}`);
                            }}
                            style={{ textAlign: "left" }}
                          >
                            {ct.name}
                          </Anchor>
                          {isPending && (
                            <Text size="xs" c="dimmed">
                              Provisioning…
                            </Text>
                          )}
                        </Stack>
                        {isPending && <Loader size="xs" />}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <StatusPill status={ct.status} />
                    </Table.Td>
                    <Table.Td>{isPending ? "—" : ct.cpu_cores || "—"}</Table.Td>
                    <Table.Td>{isPending || !ct.memory_mb ? "—" : formatRam(ct.memory_mb)}</Table.Td>
                    <Table.Td>{isPending || !ct.swap_mb ? "—" : formatRam(ct.swap_mb)}</Table.Td>
                    <Table.Td>
                      {isPending
                        ? `${formatBytes(ct.disk_total_bytes)} req.`
                        : formatBytes(ct.disk_total_bytes)}
                    </Table.Td>
                    <Table.Td>{isPending ? "—" : formatUptime(ct.uptime_seconds)}</Table.Td>
                    <Table.Td>
                      {ct.vmid !== null && (
                        <Tooltip label={backupTooltip} position="bottom" withArrow>
                          <span onClick={(e) => e.stopPropagation()}>
                            <Switch
                              size="sm"
                              checked={ct.backupEnabled}
                              disabled={
                                !ct.backupOffered ||
                                (ct.backupPaused && ct.backupEnabled) ||
                                isBusy
                              }
                              onChange={() => onBackupSwitchChange(ct)}
                              aria-label={backupTooltip}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" onClick={(e) => e.stopPropagation()} wrap="nowrap">
                        {isPending ? (
                          <Text size="xs" c="dimmed">
                            creating…
                          </Text>
                        ) : (
                          <>
                            {ct.status === "running" && (
                              <>
                                <Tooltip label="Shutdown" position="bottom" withArrow>
                                  <ActionIcon
                                    size="md"
                                    variant="default"
                                    disabled={isBusy}
                                    onClick={() => confirmPower(ct, "shutdown")}
                                    aria-label="Shutdown"
                                  >
                                    <IconPower size={15} stroke={1.75} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Stop (force)" position="bottom" withArrow>
                                  <ActionIcon
                                    size="md"
                                    variant="light"
                                    color="red"
                                    disabled={isBusy}
                                    onClick={() => confirmPower(ct, "stop")}
                                    aria-label="Stop (force)"
                                  >
                                    <IconPlayerStop size={15} stroke={1.75} />
                                  </ActionIcon>
                                </Tooltip>
                              </>
                            )}
                            {ct.status === "stopped" && (
                              <Tooltip label="Start" position="bottom" withArrow>
                                <ActionIcon
                                  size="md"
                                  variant="default"
                                  disabled={isBusy}
                                  onClick={() => void runAction(ct, "start")}
                                  aria-label="Start"
                                >
                                  <IconPlayerPlay size={15} stroke={1.75} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            {(ct.status === "orphaned" ||
                              (ct.status !== "running" && ct.status !== "stopped")) && (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Box>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <Stack align="center" justify="center" gap="md" py={64} px="md" ta="center">
      <ThemeIcon size={56} variant="light" color="brand" radius="xl">
        <IconBox size={28} stroke={1.5} />
      </ThemeIcon>
      <Stack gap={4} align="center">
        <Title order={3}>No containers yet</Title>
        <Text size="sm" c="dimmed" maw={420}>
          Deploy your first LXC container — pick an OS template, choose CPU, memory, swap, and disk.
        </Text>
      </Stack>
      <Button leftSection={<IconBox size={16} stroke={1.75} />} onClick={onCreate}>
        Deploy Container
      </Button>
    </Stack>
  );
}
